/**
 * Pure risk-scoring helpers — the single, regulator-reproducible source of truth
 * for turning factor / sub-factor bands into a 0–100 composite, a nivel, and a DDC
 * cadence. Used by the risk-matrix simulator (web) today and, going forward, the
 * scoring engine (api), so the number a compliance officer sees while calibrating
 * the matrix is the exact number the engine will produce.
 *
 * The math is a **weighted mean**, never a product or a max: each factor resolves
 * to a band (bajo/medio/alto → points), the tenant's weights combine them into the
 * composite, and the two thresholds bucket it. **Overrides** (sanciones, PEP, FATF
 * lista negra, antecedente) are a floor applied by the caller — they only ever
 * raise the nivel, and are never part of this arithmetic (Ley 23 / Acuerdo 1-2026).
 */
import { RISK_FACTOR_KEYS } from './schemas.js';
/** Points a band contributes to the weighted mean (0–100). Shared with the engine. */
export const RISK_LEVEL_POINTS = { bajo: 10, medio: 50, alto: 100 };
/** Debida diligencia del cliente (DDC) mandated by each nivel. */
export const RISK_DDC = {
    bajo: 'DDC simplificada',
    medio: 'DDC básica',
    alto: 'DDC ampliada (EDD)',
};
/** Next-review cadence (months) by nivel — mirrors the engine's CADENCE_MONTHS. */
export const RISK_CADENCE_MONTHS = { bajo: 36, medio: 24, alto: 12 };
/**
 * The next debida-diligencia renewal date for a nivel: `fromISO` (a YYYY-MM-DD
 * date) plus RISK_CADENCE_MONTHS[nivel] months, returned as a YYYY-MM-DD date.
 *
 * Pure and deterministic — the same source of truth the engine and the §3
 * renewal-alarm path both use, so the date a compliance officer sees is the date
 * the scheduler acts on. Uses UTC calendar arithmetic (no local-tz drift); a
 * month roll-over that lands past the end of the target month clamps to that
 * month's last day (e.g. 2026-01-31 + 1m → 2026-02-28), the conventional
 * add-months behaviour. `fromISO` must be a YYYY-MM-DD string.
 */
export function nextReviewDate(nivel, fromISO) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(fromISO);
    if (!m)
        throw new RangeError(`nextReviewDate: fromISO must be YYYY-MM-DD, got "${fromISO}"`);
    const year = Number(m[1]);
    const month = Number(m[2]) - 1; // 0-based month
    const day = Number(m[3]);
    const months = RISK_CADENCE_MONTHS[nivel];
    // Target year/month after adding the cadence, then clamp the day to that
    // month's last day so a shorter target month never overflows into the next.
    const totalMonths = month + months;
    const targetYear = year + Math.floor(totalMonths / 12);
    const targetMonth = ((totalMonths % 12) + 12) % 12;
    const lastDay = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
    const targetDay = Math.min(day, lastDay);
    const yyyy = String(targetYear).padStart(4, '0');
    const mm = String(targetMonth + 1).padStart(2, '0');
    const dd = String(targetDay).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}
/**
 * Weighted mean of {points, weight} items over the 0–100 scale. Divides by the
 * actual total weight, so it degrades gracefully when some weight is missing (a
 * category with no data can be dropped by the caller). Returns an unrounded value;
 * round only the final composite so nested aggregation keeps full precision.
 *
 * Divides **each term** by the total weight before summing (rather than summing the
 * products and dividing once). Mathematically equal, but this preserves the exact
 * IEEE-754 accumulation order of the pre-refactor engine — `Σ (points·weight / 100)`
 * when weights sum to 100 — so the composite is bit-for-bit identical and never flips
 * `Math.round`/nivel at a .5 boundary for a divergent tenant weight vector.
 */
export function weightedMeanPoints(items) {
    const totalWeight = items.reduce((s, i) => s + i.weight, 0);
    if (totalWeight === 0)
        return 0;
    return items.reduce((s, i) => s + (i.points * i.weight) / totalWeight, 0);
}
/** Bucket a 0–100 composite into a nivel using the two configurable thresholds. */
export function nivelFromScore(score, thresholds) {
    if (score >= thresholds.alto)
        return 'alto';
    if (score >= thresholds.medio)
        return 'medio';
    return 'bajo';
}
/**
 * Points a single family contributes, given the officer's sub-factor bands and the
 * tenant's sub-weights. When a family has sub-weights it is the weighted mean of
 * its sub-factor points; otherwise (or for producto) it is the family band directly.
 */
function familyPoints(family, levels, matrix) {
    const sub = matrix.subweights;
    if (family === 'producto')
        return RISK_LEVEL_POINTS[levels.producto];
    if (family === 'cliente') {
        const w = sub?.cliente;
        const l = levels.cliente;
        if (!w)
            return RISK_LEVEL_POINTS[maxNivel([l.profesion, l.estructura, l.antecedentes])];
        return weightedMeanPoints([
            { points: RISK_LEVEL_POINTS[l.profesion], weight: w.profesion },
            { points: RISK_LEVEL_POINTS[l.estructura], weight: w.estructura },
            { points: RISK_LEVEL_POINTS[l.antecedentes], weight: w.antecedentes },
        ]);
    }
    if (family === 'jurisdiccion') {
        const w = sub?.jurisdiccion;
        const l = levels.jurisdiccion;
        if (!w) {
            return RISK_LEVEL_POINTS[maxNivel([l.nacionalidad, l.origen_destino, l.domicilio, l.listas])];
        }
        return weightedMeanPoints([
            { points: RISK_LEVEL_POINTS[l.nacionalidad], weight: w.nacionalidad },
            { points: RISK_LEVEL_POINTS[l.origen_destino], weight: w.origen_destino },
            { points: RISK_LEVEL_POINTS[l.domicilio], weight: w.domicilio },
            { points: RISK_LEVEL_POINTS[l.listas], weight: w.listas },
        ]);
    }
    // canal
    const w = sub?.canal;
    const l = levels.canal;
    if (!w)
        return RISK_LEVEL_POINTS[maxNivel([l.canal_via, l.frecuencia, l.monto, l.instrumento])];
    return weightedMeanPoints([
        { points: RISK_LEVEL_POINTS[l.canal_via], weight: w.canal_via },
        { points: RISK_LEVEL_POINTS[l.frecuencia], weight: w.frecuencia },
        { points: RISK_LEVEL_POINTS[l.monto], weight: w.monto },
        { points: RISK_LEVEL_POINTS[l.instrumento], weight: w.instrumento },
    ]);
}
const NIVEL_ORDER = ['bajo', 'medio', 'alto'];
function maxNivel(levels) {
    return levels.reduce((a, b) => (NIVEL_ORDER.indexOf(b) > NIVEL_ORDER.indexOf(a) ? b : a), 'bajo');
}
/**
 * Compute the composite from an officer's sub-factor bands + the tenant matrix.
 * This is what the simulator runs live and what the engine will run once intake
 * captures sub-factor inputs. Overrides raise the floor to `alto` (never lower).
 */
export function computeRiskScore(matrix, levels, overrides = {}) {
    const { weights, thresholds } = matrix;
    const familyPointsMap = {
        cliente: familyPoints('cliente', levels, matrix),
        jurisdiccion: familyPoints('jurisdiccion', levels, matrix),
        canal: familyPoints('canal', levels, matrix),
        producto: familyPoints('producto', levels, matrix),
    };
    const score = Math.round(weightedMeanPoints([
        { points: familyPointsMap.cliente, weight: weights.cliente },
        { points: familyPointsMap.jurisdiccion, weight: weights.jurisdiccion },
        { points: familyPointsMap.canal, weight: weights.canal },
        { points: familyPointsMap.producto, weight: weights.producto },
    ]));
    const scoreNivel = nivelFromScore(score, thresholds);
    const overridesFired = Object.keys(overrides).filter((k) => overrides[k]);
    const nivel = overridesFired.length > 0 ? 'alto' : scoreNivel;
    return {
        score,
        scoreNivel,
        nivel,
        ddc: RISK_DDC[nivel],
        cadenciaMeses: RISK_CADENCE_MONTHS[nivel],
        familyPoints: familyPointsMap,
        overridesFired,
    };
}
export const INDICATOR_APPLICABILITY = {
    financiero: {
        families: ['cliente', 'jurisdiccion', 'canal', 'producto'],
        disabledSubfactors: [],
    },
    abogados: {
        // producto is dropped wholesale (bank concept); canal keeps only canal_via.
        families: ['cliente', 'jurisdiccion', 'canal'],
        disabledSubfactors: ['canal.monto', 'canal.frecuencia', 'canal.instrumento'],
    },
    // Mercado de valores (SMV), seguros (SSRP) e inmobiliaria (SNF): cada uno es su
    // propia clase de sujeto obligado, con screening completo (las cuatro familias),
    // igual que financiero. Sus pesos por defecto los da su preset por sector.
    valores: {
        families: ['cliente', 'jurisdiccion', 'canal', 'producto'],
        disabledSubfactors: [],
    },
    seguros: {
        families: ['cliente', 'jurisdiccion', 'canal', 'producto'],
        disabledSubfactors: [],
    },
    inmobiliaria: {
        families: ['cliente', 'jurisdiccion', 'canal', 'producto'],
        disabledSubfactors: [],
    },
};
/**
 * Distribute 100 across `values` in proportion to their integer inputs, returning
 * integers that sum to exactly 100 (largest-remainder / Hamilton method). Floors
 * each proportional share, then hands the leftover units to the largest fractional
 * remainders, ties broken by array order — fully deterministic, so the effective
 * matrix is reproducible. When every input is 0, spreads 100 as evenly as possible.
 */
function renormalizeTo100(values) {
    const n = values.length;
    if (n === 0)
        return [];
    const total = values.reduce((s, v) => s + v, 0);
    const shares = total === 0 ? values.map(() => 100 / n) : values.map((v) => (v * 100) / total);
    const floors = shares.map((s) => Math.floor(s));
    let remainder = 100 - floors.reduce((s, v) => s + v, 0);
    const order = shares
        .map((s, i) => ({ i, frac: s - Math.floor(s) }))
        .sort((a, b) => (b.frac !== a.frac ? b.frac - a.frac : a.i - b.i));
    const result = [...floors];
    for (let k = 0; k < order.length && remainder > 0; k++) {
        result[order[k].i] += 1;
        remainder -= 1;
    }
    return result;
}
/**
 * Re-normalize one sub-family's sub-weights over the sub-factors that remain active,
 * so a disabled sub-factor drops out of its family's weighted mean (its share is
 * redistributed via Hamilton). Preserves the group's exact shape (all keys kept;
 * inactive ones set to 0). When nothing is disabled and the group already sums to
 * 100, `renormalizeTo100` is the identity, so the group is unchanged bit-for-bit.
 * When EVERY sub-factor is disabled the group is left raw (the caller should have
 * dropped the whole family via `families`, in which case its weight is 0 anyway).
 */
function renormSubgroup(group, family, disabled) {
    const keys = Object.keys(group);
    const activeSubs = keys.filter((k) => !disabled.has(`${family}.${k}`));
    if (activeSubs.length === 0)
        return { ...group };
    const renormSub = renormalizeTo100(activeSubs.map((k) => group[k] ?? 0));
    const out = {};
    for (const k of keys)
        out[k] = 0;
    activeSubs.forEach((k, i) => {
        out[k] = renormSub[i] ?? 0;
    });
    return out;
}
/**
 * Reduce a tenant matrix to the families/sub-factors that apply in `mode` (merged
 * with the tenant's own `userDisabled` on/off choices), with the remaining top-level
 * weights and every sub-family's sub-weights re-normalized to sum 100, so
 * `computeRiskScore(effectiveMatrixForMode(mode, cfg, userDisabled), levels, overrides)`
 * stays schema-valid and the weighted mean still runs over 100 — WITHOUT touching
 * `computeRiskScore` itself. A disabled family's weight is set to 0 (so it drops out
 * of the mean) and its share is redistributed over the active families.
 *
 * When nothing is pruned (financiero with no tenant edits) the config is returned
 * unchanged (the same reference), which keeps scoring bit-identical to today.
 */
export function effectiveMatrixForMode(mode, cfg, userDisabled) {
    const applies = INDICATOR_APPLICABILITY[mode];
    // Merge the mode's built-in applicability with the tenant's own on/off choices.
    const disabledFamilies = new Set([
        ...RISK_FACTOR_KEYS.filter((f) => !applies.families.includes(f)),
        ...(userDisabled?.families ?? []),
    ]);
    const disabled = new Set([
        ...applies.disabledSubfactors,
        ...(userDisabled?.subfactors ?? []),
    ]);
    // Nothing pruned → unchanged (financiero w/o tenant edits: bit-identical).
    if (disabledFamilies.size === 0 && disabled.size === 0)
        return cfg;
    const activeFamily = (f) => !disabledFamilies.has(f);
    // Re-normalize the top-level weights over the active families (inactive → 0).
    const activeWeights = RISK_FACTOR_KEYS.filter(activeFamily).map((f) => cfg.weights[f]);
    const renorm = renormalizeTo100(activeWeights);
    const weightByFamily = {};
    let ai = 0;
    for (const f of RISK_FACTOR_KEYS) {
        weightByFamily[f] = activeFamily(f) ? renorm[ai++] : 0;
    }
    const weights = {
        cliente: weightByFamily.cliente,
        jurisdiccion: weightByFamily.jurisdiccion,
        canal: weightByFamily.canal,
        producto: weightByFamily.producto,
    };
    // Re-normalize the sub-weights of every sub-family present, over its active
    // sub-factors. exactOptionalPropertyTypes: only carry the groups that exist.
    // renormSubgroup keeps the group's exact keys, so the shape stays valid; the cast
    // just re-attaches the specific RiskSubweights typing the compiler can't infer
    // through the string-keyed helper.
    let subweights = cfg.subweights;
    const s = cfg.subweights;
    if (s) {
        const out = {};
        if (s.cliente)
            out.cliente = renormSubgroup(s.cliente, 'cliente', disabled);
        if (s.jurisdiccion)
            out.jurisdiccion = renormSubgroup(s.jurisdiccion, 'jurisdiccion', disabled);
        if (s.canal)
            out.canal = renormSubgroup(s.canal, 'canal', disabled);
        subweights = out;
    }
    return {
        weights,
        thresholds: cfg.thresholds,
        ...(subweights ? { subweights } : {}),
    };
}
export const RISK_FACTOR_REFERENCE = [
    {
        key: 'cliente',
        label: 'Cliente',
        fundamento: 'Acuerdo 1-2026 Art. 13.1; Ley 23 Art. 26',
        explicacion: 'Quién es el cliente: su actividad, su estructura jurídica y su historial. Es la familia de mayor peso porque concentra las vías clásicas de ocultamiento del beneficiario final.',
        subfactors: [
            {
                key: 'profesion',
                label: 'Profesión / actividad económica',
                fundamento: 'Ley 124/2020 Art. 40 (APNFD); FATF R.22',
                explicacion: 'Mide si la actividad del cliente está en la lista APNFD de la Ley 124/2020 (Art. 40) o es intensiva en efectivo. Las actividades "gatekeeper" (crear sociedades, agente residente, manejar fondos ajenos) son la vía clásica de ocultamiento del beneficiario final; una actividad sin fuente de ingreso verificable también es señal.',
                bands: {
                    bajo: 'Asalariado, jubilado, profesión no listada',
                    medio: 'Independiente; comercio intensivo en efectivo; sin actividad verificable',
                    alto: 'Gatekeeper (abogado/contador/notario), casino, inmobiliaria, metales, cambio/remesas',
                },
            },
            {
                key: 'estructura',
                label: 'Tipo de persona / estructura jurídica',
                fundamento: 'Ley 23 Art. 28; FATF INR.10',
                explicacion: 'Mide cuántas capas hay entre el activo y la persona real: holdings de tenencia, fundaciones de interés privado, fideicomisos, nominees o acciones al portador elevan el riesgo porque el dueño real puede no aparecer (FATF INR.10).',
                bands: {
                    bajo: 'PN asalariada con ingreso verificable',
                    medio: 'PN independiente; PJ operativa con BO claro',
                    alto: 'Holding/tenencia, fundación de interés privado, fideicomiso, acciones al portador, nominees',
                },
            },
            {
                key: 'antecedentes',
                label: 'Antecedentes penales / medios adversos',
                fundamento: 'UAF Catálogo de Señales; CP Art. 254 (señal, no decisión adversa automática)',
                explicacion: 'Coteja al cliente contra sentencias penales (fuente: fallos del Ministerio Público / Órgano Judicial) y medios adversos. Solo la condena firme por un delito precedente del blanqueo (CP Art. 254) pesa como alto; nunca produce rechazo automático — siempre revisión humana (debido proceso).',
                bands: {
                    bajo: 'Sin antecedente conocido',
                    medio: 'Proceso abierto (no precedente) o medios adversos en revisión',
                    alto: 'Condena firme por delito precedente de blanqueo',
                },
            },
        ],
    },
    {
        key: 'jurisdiccion',
        label: 'Jurisdicción',
        aka: 'Geografía',
        fundamento: 'Acuerdo 1-2026 Art. 13.2; Art. 20 in fine num. 3 y Art. 21',
        explicacion: 'Dónde está el cliente y por dónde se mueven sus fondos: nacionalidad, origen/destino de recursos, domicilio y pertenencia del país a listas internacionales.',
        subfactors: [
            {
                key: 'nacionalidad',
                label: 'Nacionalidad / país de constitución',
                fundamento: 'Acuerdo 1-2026 Art. 13.2 lit. a y d',
                explicacion: 'País del pasaporte (persona natural) o de constitución (persona jurídica). Se puntúa con índices creíbles (Basel AML Index, CPI); su peso está acotado para no penalizar en exceso a extranjeros legítimos — el salto fuerte lo dan las listas.',
                bands: {
                    bajo: 'Panamá / país de bajo riesgo (Basel bajo, CPI alto)',
                    medio: 'País estándar; CPI bajo',
                    alto: 'Jurisdicción de secrecía o alto riesgo por índice',
                },
            },
            {
                key: 'origen_destino',
                label: 'Origen / destino de los recursos',
                fundamento: 'Acuerdo 1-2026 Art. 13.2 lit. b y c',
                explicacion: 'De dónde vienen y hacia dónde van los fondos del cliente. Fondos desde o hacia países de alto riesgo o no cooperantes elevan el riesgo aunque el cliente sea local.',
                bands: {
                    bajo: 'Fondos locales / país de bajo riesgo',
                    medio: 'Fondos desde/hacia país estándar',
                    alto: 'Fondos desde/hacia país de alto riesgo o no cooperante',
                },
            },
            {
                key: 'domicilio',
                label: 'País de domicilio / operación',
                fundamento: 'Acuerdo 1-2026 Art. 13.2 lit. e',
                explicacion: 'Dónde vive u opera realmente el cliente — puede diferir de su nacionalidad y revela exposición operativa a jurisdicciones monitoreadas.',
                bands: {
                    bajo: 'Domicilio y operación en Panamá',
                    medio: 'Operación regional estándar',
                    alto: 'Operación en jurisdicción monitoreada',
                },
            },
            {
                key: 'listas',
                label: 'Listas internacionales del país',
                fundamento: 'Acuerdo 1-2026 Art. 13 (evasión fiscal) y Art. 20 in fine num. 3 / Art. 21; FATF R.19; Consejo UE lista fiscal (Anexos I/II); UE lista AML Reg. (UE) 2016/1675; Basel AML Index',
                explicacion: 'Pertenencia del país a listas internacionales versionadas: GAFI gris (monitoreo intensificado) y el Anexo I fiscal del Consejo UE pesan alto; el Anexo II fiscal (compromisos pendientes) pesa medio. La lista AML de la UE (Reg. 2016/1675) es una fuente separada donde todo país listado pesa alto. La lista NEGRA de GAFI (Irán, RPDC, Myanmar) y las sanciones de país NO se ponderan aquí: son override que fuerza el nivel más alto.',
                bands: {
                    bajo: 'No figura en ninguna lista',
                    medio: 'UE Anexo II (gris fiscal); compromisos pendientes',
                    alto: 'GAFI lista gris; UE Anexo I; Basel banda alta',
                },
            },
        ],
    },
    {
        key: 'producto',
        label: 'Producto',
        fundamento: 'Acuerdo 1-2026 Art. 13.3 (variable única; tiering por producto — calibración FATF)',
        explicacion: 'Qué producto o servicio contrata el cliente. Es variable única en la norma: el instrumento mismo puede ser el riesgo (efectivo, cripto, banca privada, corresponsalía).',
        subfactors: [],
        bands: {
            bajo: 'Cuenta básica / ahorro de bajo saldo',
            medio: 'Crédito / inversión',
            alto: 'Efectivo, criptoactivos, comercio exterior, banca privada, corresponsalía',
        },
    },
    {
        key: 'canal',
        label: 'Canal',
        aka: 'Transaccionalidad / Canal',
        fundamento: 'Acuerdo 1-2026 Art. 13.4',
        explicacion: 'Cómo se vincula y transacciona el cliente: la vía de onboarding, el patrón de operaciones, los montos frente al perfil y el instrumento usado.',
        subfactors: [
            {
                key: 'canal_via',
                label: 'Canal (presencial vs remoto)',
                fundamento: 'Acuerdo 1-2026 Art. 13.4 lit. d; Art. 14; Art. 20',
                explicacion: 'La vía de vinculación. El onboarding remoto solo pesa alto cuando NO hay mitigantes (geolocalización inferencial obligatoria del Art. 14 + verificación reforzada de identidad del Art. 20) — un remoto bien verificado queda en medio.',
                bands: {
                    bajo: 'Presencial',
                    medio: 'Remoto verificado (geolocalización inferencial + verificación de identidad)',
                    alto: 'Remoto no verificado / vía intermediario',
                },
            },
            {
                key: 'frecuencia',
                label: 'Frecuencia / velocidad',
                fundamento: 'Acuerdo 1-2026 Art. 13.4 lit. c; UAF Señales',
                explicacion: 'El patrón temporal de las operaciones. La estructuración (fraccionar bajo el umbral), las ráfagas y las cuentas dormidas reactivadas son señales expresas del catálogo UAF.',
                bands: {
                    bajo: 'Estable y congruente',
                    medio: 'Variable pero explicada',
                    alto: 'Estructuración / fraccionamiento; ráfagas; cuenta reactivada',
                },
            },
            {
                key: 'monto',
                label: 'Monto vs perfil',
                fundamento: 'Acuerdo 1-2026 Art. 13.4 lit. b (umbral RTE B/.10,000 es reporte, no peso)',
                explicacion: 'Si los montos son congruentes con el perfil declarado del cliente. El umbral RTE de B/.10,000 obliga a reportar, no a ponderar: lo que pesa es la incongruencia.',
                bands: {
                    bajo: 'Congruente con el perfil',
                    medio: 'Sobre umbral RTE',
                    alto: 'Múltiplos del umbral o incongruente con el perfil',
                },
            },
            {
                key: 'instrumento',
                label: 'Instrumento',
                fundamento: 'Acuerdo 1-2026 Art. 13.4 lit. a; Art. 28',
                explicacion: 'Con qué se paga: la transferencia trazable pesa bajo, el cheque medio, y el efectivo/cuasi-efectivo o cripto alto porque rompen la trazabilidad.',
                bands: {
                    bajo: 'Transferencia trazable',
                    medio: 'Cheque (semi-trazable)',
                    alto: 'Efectivo / cuasi-efectivo / cripto',
                },
            },
        ],
    },
];
/**
 * Filtra un catálogo a las entradas aplicables a un tipo de sujeto obligado.
 * Una entrada sin `modes` es universal; con `modes`, solo aparece en esos tipos.
 * El simulador y el editor de indicadores usan esto para mostrar, por tipo, solo
 * los indicadores que le corresponden.
 */
export function catalogForMode(entries, mode) {
    return entries.filter((e) => !e.modes || e.modes.includes(mode));
}
/** Profession / economic-activity catalog → band for the cliente.profesion sub-factor. */
export const RISK_PROFESSION_CATALOG = [
    { key: 'asalariado', label: 'Asalariado / jubilado (ingreso verificable)', band: 'bajo', fundamento: 'Fuente de ingreso verificable; no listado en Art. 40' },
    { key: 'tecnica', label: 'Profesión técnica (médico, ingeniero, TI, agro)', band: 'bajo', fundamento: 'No listada en Art. 40 ni FATF R.22; el bajo es calibración' },
    { key: 'minorista', label: 'Minorista / consumo masivo (no efectivo-intensivo)', band: 'bajo', fundamento: 'No listado en Art. 40' },
    { key: 'abogado_litigio', label: 'Abogado — solo litigio / asesoría pura', band: 'bajo', fundamento: 'Fuera de las actividades del Art. 40.11; conserva KYC mínimo' },
    { key: 'sin_actividad', label: 'Estudiante / sin actividad declarada o verificable', band: 'medio', fundamento: 'Ausencia de fuente verificable = señal (calibración)' },
    { key: 'efectivo_intensivo', label: 'Comercio intensivo en efectivo (restaurante, estacionamiento…)', band: 'medio', fundamento: 'FATF INR.10 "cash-intensive"' },
    { key: 'contador_auditoria', label: 'Contador — auditoría / impuestos (sin manejo de fondos)', band: 'medio', fundamento: 'Obligado solo en las actividades enumeradas del Art. 40.11 (contadores públicos autorizados; fallo Corte Suprema)' },
    { key: 'notario', label: 'Notario', band: 'medio', fundamento: 'Ley 124/2020 Art. 40.13; FATF R.22; escrituras de constitución/transferencia' },
    { key: 'abogado_gatekeeper', label: 'Abogado — sociedades, agente residente, manejo de fondos', band: 'alto', fundamento: 'Ley 124/2020 Art. 40.11; FATF R.22 (gatekeeper)' },
    { key: 'contador_fondos', label: 'Contador — manejo de fondos / creación de sociedades', band: 'alto', fundamento: 'Ley 124/2020 Art. 40.11 (contadores; fallo Corte Suprema los ubica junto a abogados en el num. 11)' },
    { key: 'tcsp', label: 'Proveedor fiduciario y societario (TCSP)', band: 'alto', fundamento: 'Art. 40.11.e/j/k; FATF R.22' },
    { key: 'inmobiliaria', label: 'Promotor / corredor inmobiliario', band: 'alto', fundamento: 'Art. 40.3' },
    { key: 'construccion', label: 'Construcción / contratista', band: 'alto', fundamento: 'Art. 40.4; ENR' },
    { key: 'casino', label: 'Casino / juegos de azar / apuestas', band: 'alto', fundamento: 'Art. 40.2' },
    { key: 'metales', label: 'Metales y piedras preciosas / joyería', band: 'alto', fundamento: 'Art. 40.7; FATF R.22(c)' },
    { key: 'empeno', label: 'Casa de empeño', band: 'alto', fundamento: 'Art. 40.6' },
    { key: 'autos', label: 'Comerciante de autos (efectivo / alto valor)', band: 'alto', fundamento: 'Art. 40.10' },
    { key: 'zona_libre', label: 'Zona Libre de Colón / zonas francas', band: 'alto', fundamento: 'Art. 40.1 (TBML)' },
    { key: 'cambio_remesas', label: 'Casa de cambio / remesas', band: 'alto', fundamento: 'Ley 23 Art. 22.1.h/i (remesas y casas de cambio, sujetos financieros SBP); ENR' },
    { key: 'transporte_valores', label: 'Transporte de valores', band: 'alto', fundamento: 'Art. 40.5' },
    { key: 'vasp', label: 'VASP / criptoactivos', band: 'alto', fundamento: 'FATF R.15 (sin régimen vigente en PA)' },
    // — Señales de cliente por tipo (transversales; informe sec. 3.3). Se cuelgan de
    //   `profesión` (single-select): el oficial elige la señal dominante. ponytail:
    //   reutiliza el subfactor existente en vez de crear uno multi-señal (eso es el
    //   upgrade grande, no bloqueante). `modes` las poda por tipo en el simulador.
    { key: 'no_residente_offshore', label: 'No residente que estructura patrimonio offshore (sin nexo económico local)', band: 'alto', modes: ['abogados', 'inmobiliaria', 'financiero'], fundamento: 'FATF R.22 + INR.10; Ley 23 Art. 27 — perfil de mayor riesgo del gatekeeper panameño' },
    { key: 'canaliza_fondos_terceros', label: 'Canaliza / aporta fondos de terceros (mueve dinero ajeno)', band: 'alto', modes: ['abogados'], fundamento: 'Ley 124/2020 Art. 40.11; FATF R.22(b)' },
    { key: 'tercero_pagador_seguros', label: 'Tercero pagador de la prima distinto del contratante', band: 'medio', modes: ['seguros'], fundamento: 'Acuerdo SSRP 07-2025 Art. 21; señal UAF de persona interpuesta' },
    { key: 'tercero_pagador_inmobiliaria', label: 'Tercero pagador distinto del comprador en el cierre', band: 'alto', modes: ['inmobiliaria'], fundamento: 'Señal UAF de persona interpuesta en la compraventa; FATF ENR inmobiliario' },
    { key: 'beneficiario_cesionario_poliza', label: 'Beneficiario / cesionario de la póliza (tercero designado)', band: 'medio', modes: ['seguros'], fundamento: 'Acuerdo SSRP 07-2025 Art. 3.4 + Art. 18; designación/cesión a tercero = señal AML' },
    { key: 'fuente_fondos_no_acreditable', label: 'Fuente de fondos no acreditable (origen difuso / efectivo)', band: 'alto', modes: ['inmobiliaria'], fundamento: 'FATF ENR inmobiliario; el origen de fondos es el control central de la compraventa' },
    // — Perfil de comprador inmobiliario (dueño, 2026-07-13): el régimen de interés
    //   preferencial filtra el perfil — precio topado + underwriting bancario del
    //   salario ("la gente gana lo que dice"); sin él, el comprador es comercial y
    //   el origen de fondos requiere análisis propio.
    { key: 'comprador_interes_preferencial', label: 'Comprador de vivienda con interés preferencial (asalariado, ingreso verificado)', band: 'bajo', modes: ['inmobiliaria'], fundamento: 'Régimen de interés preferencial (Ley 3/1985 y reformas): tope de precio + underwriting bancario del salario — ingreso congruente doblemente verificado' },
    { key: 'comprador_comercial', label: 'Comprador comercial (sin interés preferencial)', band: 'medio', modes: ['inmobiliaria'], fundamento: 'Sin el filtro del régimen preferencial, el origen de fondos requiere análisis propio (calibración)' },
    { key: 'inversionista_inmobiliario', label: 'Inversionista inmobiliario (compra para renta / reventa / plusvalía)', band: 'medio', modes: ['inmobiliaria'], fundamento: 'FATF ENR: la compra de inversión es tipología de integración; las variantes de mayor riesgo llevan señal propia (no residente offshore, fuente no acreditable, tercero pagador)' },
];
/** Country list-membership catalog → band for the jurisdiccion.listas sub-factor. */
export const RISK_COUNTRY_LIST_CATALOG = [
    { key: 'ninguna', label: 'No figura en ninguna lista', band: 'bajo', fundamento: 'Sin membresía en listas internacionales' },
    { key: 'anexo_ii', label: 'UE Anexo II (gris fiscal, compromisos pendientes)', band: 'medio', fundamento: 'UE Anexo II' },
    { key: 'cpi_bajo', label: 'CPI bajo / percepción de corrupción elevada', band: 'medio', fundamento: 'CPI Transparencia Internacional (insumo, no lista dura)' },
    { key: 'fatf_gris', label: 'GAFI lista GRIS (monitoreo intensificado)', band: 'alto', fundamento: 'FATF R.19 (lista de monitoreo intensificado, corte 19-jun-2026); Acuerdo 1-2026 Art. 20 in fine num. 3 y Art. 21' },
    { key: 'anexo_i', label: 'UE Anexo I (no cooperante fiscal)', band: 'alto', fundamento: 'Consejo de la UE, lista fiscal de jurisdicciones no cooperantes, Anexo I (act. 17-feb-2026); Acuerdo 1-2026 Art. 13' },
    { key: 'basel_alto', label: 'Basel AML Index banda alta', band: 'alto', fundamento: 'Basel AML Index (17 indicadores, 14ª edición 2025)' },
    { key: 'fatf_negra', label: 'GAFI lista NEGRA (Irán, RPDC, Myanmar)', band: 'alto', fundamento: 'FATF R.19 Call for Action + INR.19 párr. 2(e) (contramedidas para Irán/RPDC; EDD reforzada para Myanmar) → OVERRIDE a crítico; congelamiento de designados vía Ley 23 Arts. 49-52 + DE 587/2015 (listas CSNU), no de la R.19', override: true },
];
/** Legal-structure catalog → band for cliente.estructura (Ley 23 Art. 28; FATF INR.10). */
export const RISK_ESTRUCTURA_CATALOG = [
    { key: 'pn_asalariada', label: 'Persona natural asalariada (ingreso verificable)', band: 'bajo', fundamento: 'Sin capas: el titular es el dueño real' },
    { key: 'pn_independiente', label: 'Persona natural independiente / comerciante', band: 'medio', fundamento: 'Ingreso variable; el perfil requiere corroboración' },
    { key: 'pj_operativa', label: 'PJ operativa con beneficiario final claro', band: 'medio', fundamento: 'Ley 23 Art. 28: BO identificable en primer nivel' },
    { key: 'sa_nominativa', label: 'S.A. panameña nominativa con actividad real', band: 'medio', fundamento: 'Estructura estándar; BO verificable en el RUBF (Ley 129/2020)' },
    { key: 'holding', label: 'Holding / sociedad de tenencia de activos', band: 'alto', fundamento: 'FATF INR.10: capa de tenencia entre el activo y el dueño real' },
    { key: 'fundacion', label: 'Fundación de interés privado', band: 'alto', fundamento: 'Vehículo sin accionistas; fundador y beneficiarios pueden no aparecer' },
    { key: 'fideicomiso', label: 'Fideicomiso', band: 'alto', fundamento: 'Separación fiduciaria del patrimonio (FATF R.25)' },
    { key: 'multinivel', label: 'Estructura multinivel / offshore / nominees', band: 'alto', fundamento: 'Capas múltiples entre el activo y el BO (FATF INR.10)' },
    { key: 'portador', label: 'Acciones al portador (en custodia, Ley 47/2013)', band: 'alto', fundamento: 'Máxima opacidad histórica; custodia obligatoria vigente' },
];
/** Criminal-record / adverse-media catalog → band for cliente.antecedentes (UAF Señales; CP Art. 254). */
export const RISK_ANTECEDENTES_CATALOG = [
    { key: 'sin_antecedentes', label: 'Sin antecedentes ni medios adversos conocidos', band: 'bajo', fundamento: 'Sin señal en fallos ni medios' },
    { key: 'adversos_revision', label: 'Medios adversos no corroborados (en revisión)', band: 'medio', fundamento: 'Señal UAF: requiere corroboración humana, nunca decisión automática' },
    { key: 'proceso_abierto', label: 'Proceso abierto por delito NO precedente', band: 'medio', fundamento: 'Presunción de inocencia; pesa como señal' },
    { key: 'condena_no_precedente', label: 'Condena por delito no precedente del blanqueo', band: 'medio', fundamento: 'Fuera del catálogo del CP Art. 254; señal de contexto' },
    { key: 'condena_precedente', label: 'Condena firme por delito precedente (CP Art. 254)', band: 'alto', fundamento: 'Delito precedente del blanqueo (CP Arts. 254 y 254-A) → OVERRIDE: piso alto + EDD (calibración del producto; ninguna norma fija el nivel); siempre revisión humana', override: true },
];
/**
 * Per-country risk catalog (corte **8-jul-2026**: GAFI negra/gris del 19-jun-2026, UE
 * lista AML Reg. 2016/1675 consolidado 29-ene-2026 + Regs. 2026/46 y 2026/83, UE lista
 * fiscal 17-feb-2026, OFAC vigente a la fecha — las listas son versionadas; verifique la
 * lista vigente antes de decidir). Band = calibración de referencia compuesta de: listas
 * GAFI (negra → OVERRIDE, gris → alto), UE lista AML (todo país listado → alto), sanciones
 * OFAC de país (integrales → OVERRIDE; selectivas no promedian solas), Basel AML Index, CPI
 * y centros de secrecía. Keys are ISO-3166 alpha-2 (no distinguen sub-regiones — ver nota en
 * 'ua' sobre Crimea/Donetsk/Luhansk). Shared by the three geography sub-factors.
 */
export const RISK_COUNTRY_RISK_CATALOG = [
    { key: 'pa', label: 'Panamá', band: 'bajo', fundamento: 'Plaza local; fuera de la gris GAFI desde oct-2023 y de la lista AML UE desde ago-2025 (Reg. 2025/1184); permanece en el Anexo I fiscal UE (17-feb-2026) — no se pondera aquí (catálogo AML, no fiscal)' },
    // — GAFI negra / sanciones integrales → OVERRIDE, no ponderación (FATF R.19) —
    { key: 'ir', label: 'Irán', band: 'alto', fundamento: 'GAFI lista NEGRA — contramedidas (R.19/INR.19 §2(e)) + congelamiento ONU de designados (Ley 23 Art. 49, Res. 1737) → override', override: true },
    { key: 'kp', label: 'Corea del Norte (RPDC)', band: 'alto', fundamento: 'GAFI lista NEGRA — contramedidas (R.19/INR.19 §2(e)) + congelamiento ONU de designados (Ley 23 Art. 49, Res. 1718) → override', override: true },
    { key: 'mm', label: 'Myanmar', band: 'alto', fundamento: 'GAFI lista NEGRA — llamado a EDD reforzada (sin contramedidas, jun-2026); override/bloqueo es política interna del sujeto obligado (sin régimen ONU de país)', override: true },
    { key: 'cu', label: 'Cuba', band: 'alto', fundamento: 'Sanciones integrales de país (OFAC) → override (política interna, no vinculante en Panamá)', override: true },
    // — alto —
    { key: 'sy', label: 'Siria', band: 'alto', fundamento: 'GAFI gris (19-jun-2026); UE lista AML Reg. 2016/1675 punto I; OFAC ya solo selectivo (PAARSS) — el programa integral fue revocado por la EO 14312 (30-jun-2025)' },
    { key: 've', label: 'Venezuela', band: 'alto', fundamento: 'GAFI gris (19-jun-2026); UE lista AML Reg. 2016/1675 punto I; sanciones OFAC selectivas (Venezuela-Related); CPI muy bajo' },
    { key: 'ru', label: 'Rusia', band: 'alto', fundamento: 'Membresía GAFI suspendida (2023); sanciones amplias' },
    { key: 'by', label: 'Bielorrusia', band: 'alto', fundamento: 'Sanciones UE/OFAC; alineamiento con Rusia' },
    { key: 'af', label: 'Afganistán', band: 'alto', fundamento: 'Sanciones; sistema financiero sin supervisión efectiva' },
    { key: 'ye', label: 'Yemen', band: 'alto', fundamento: 'GAFI gris (corte jun 2026); conflicto activo' },
    { key: 'sd', label: 'Sudán', band: 'alto', fundamento: 'Conflicto activo; sanciones dirigidas' },
    { key: 'ss', label: 'Sudán del Sur', band: 'alto', fundamento: 'GAFI gris (corte jun 2026)' },
    { key: 'so', label: 'Somalia', band: 'alto', fundamento: 'Basel AML Index banda alta; supervisión mínima' },
    { key: 'ly', label: 'Libia', band: 'alto', fundamento: 'Conflicto; sanciones dirigidas' },
    { key: 'iq', label: 'Irak', band: 'alto', fundamento: 'GAFI gris (añadido 19-jun-2026); Basel banda alta' },
    { key: 'ht', label: 'Haití', band: 'alto', fundamento: 'GAFI gris (corte jun 2026)' },
    { key: 'lb', label: 'Líbano', band: 'alto', fundamento: 'GAFI gris (corte jun 2026); crisis bancaria' },
    { key: 'cd', label: 'RD del Congo', band: 'alto', fundamento: 'GAFI gris (corte jun 2026)' },
    { key: 'vn', label: 'Vietnam', band: 'alto', fundamento: 'GAFI gris (corte jun 2026)' },
    { key: 'mc', label: 'Mónaco', band: 'alto', fundamento: 'GAFI gris (corte jun 2026); centro de secrecía' },
    { key: 'dz', label: 'Argelia', band: 'alto', fundamento: 'UE lista AML Reg. 2016/1675 punto I (Reg. 2026/83); salió de la gris GAFI el 19-jun-2026' },
    { key: 'ao', label: 'Angola', band: 'alto', fundamento: 'GAFI gris (corte jun 2026)' },
    { key: 'ci', label: 'Costa de Marfil', band: 'alto', fundamento: 'GAFI gris (corte jun 2026)' },
    { key: 'cm', label: 'Camerún', band: 'alto', fundamento: 'GAFI gris (corte jun 2026)' },
    { key: 'ke', label: 'Kenia', band: 'alto', fundamento: 'GAFI gris (corte jun 2026)' },
    { key: 'np', label: 'Nepal', band: 'alto', fundamento: 'GAFI gris (corte jun 2026)' },
    { key: 'vg', label: 'Islas Vírgenes Británicas', band: 'alto', fundamento: 'GAFI gris (19-jun-2026); UE lista AML Reg. 2016/1675 punto I (Reg. 2026/83); UE Anexo II fiscal' },
    { key: 'ni', label: 'Nicaragua', band: 'alto', fundamento: 'CPI muy bajo; sanciones dirigidas' },
    { key: 'bo', label: 'Bolivia', band: 'alto', fundamento: 'GAFI gris (19-jun-2026); UE lista AML Reg. 2016/1675 punto I (Reg. 2026/83)' },
    { key: 'tt', label: 'Trinidad y Tobago', band: 'alto', fundamento: 'UE lista AML Reg. 2016/1675 punto I (Reg. 2026/83)' },
    { key: 'ba', label: 'Bosnia y Herzegovina', band: 'alto', fundamento: 'GAFI gris (añadida 19-jun-2026)' },
    { key: 'bg', label: 'Bulgaria', band: 'alto', fundamento: 'GAFI gris (19-jun-2026); Estado miembro UE (no listable en la lista AML Reg. 2016/1675, que solo cubre terceros países)' },
    { key: 'kw', label: 'Kuwait', band: 'alto', fundamento: 'GAFI gris (19-jun-2026)' },
    { key: 'la', label: 'Laos', band: 'alto', fundamento: 'GAFI gris (19-jun-2026); UE lista AML Reg. 2016/1675 punto I (Reg. 2026/83)' },
    { key: 'pg', label: 'Papúa Nueva Guinea', band: 'alto', fundamento: 'GAFI gris (19-jun-2026)' },
    { key: 'vu', label: 'Vanuatu', band: 'alto', fundamento: 'UE lista AML Reg. 2016/1675 punto I (Reg. 2026/83); UE Anexo I fiscal (17-feb-2026)' },
    { key: 'na', label: 'Namibia', band: 'alto', fundamento: 'UE lista AML Reg. 2016/1675 punto I (Reg. 2026/83); salió de la gris GAFI el 19-jun-2026' },
    // — medio —
    { key: 'kz', label: 'Kazajistán', band: 'medio', fundamento: 'CPI bajo; corredor comercial/financiero con Rusia' },
    { key: 'mx', label: 'México', band: 'medio', fundamento: 'Flujos ilícitos y efectivo; CPI bajo' },
    { key: 'co', label: 'Colombia', band: 'medio', fundamento: 'Economías ilícitas; corredor regional' },
    { key: 'br', label: 'Brasil', band: 'medio', fundamento: 'CPI medio-bajo; volumen regional' },
    { key: 'do', label: 'República Dominicana', band: 'medio', fundamento: 'CPI bajo; corredor de remesas' },
    { key: 'gt', label: 'Guatemala', band: 'medio', fundamento: 'CPI bajo; efectivo transfronterizo' },
    { key: 'hn', label: 'Honduras', band: 'medio', fundamento: 'CPI bajo; corredor de remesas' },
    { key: 'sv', label: 'El Salvador', band: 'medio', fundamento: 'CPI bajo; criptoactivos de curso legal' },
    { key: 'ec', label: 'Ecuador', band: 'medio', fundamento: 'Economías ilícitas emergentes; dolarizado' },
    { key: 'pe', label: 'Perú', band: 'medio', fundamento: 'CPI bajo; minería informal' },
    { key: 'py', label: 'Paraguay', band: 'medio', fundamento: 'TBML Triple Frontera; efectivo' },
    { key: 'mz', label: 'Mozambique', band: 'medio', fundamento: 'Salió de la gris GAFI (no figura en la lista de 19-jun-2026); removido de la lista AML UE (Reg. 2026/83)' },
    { key: 'ng', label: 'Nigeria', band: 'medio', fundamento: 'Salió de la gris GAFI (no figura en la lista de 19-jun-2026); removida de la lista AML UE (Reg. 2026/83)' },
    { key: 'za', label: 'Sudáfrica', band: 'medio', fundamento: 'Salió de la gris GAFI (no figura en la lista de 19-jun-2026); removida de la lista AML UE (Reg. 2026/83)' },
    { key: 'bf', label: 'Burkina Faso', band: 'medio', fundamento: 'Salió de la gris GAFI (no figura en la lista de 19-jun-2026); removido de la lista AML UE (Reg. 2026/83)' },
    { key: 'ml', label: 'Malí', band: 'medio', fundamento: 'Salió de la gris GAFI; removido de la lista AML UE (Reg. 2026/83); sanciones OFAC selectivas Mali-Related' },
    { key: 'tz', label: 'Tanzania', band: 'medio', fundamento: 'Salió de la gris GAFI (no figura en la lista de 19-jun-2026); removida de la lista AML UE (Reg. 2026/83)' },
    { key: 'ar', label: 'Argentina', band: 'medio', fundamento: 'Controles cambiarios; mercado paralelo' },
    { key: 'cn', label: 'China', band: 'medio', fundamento: 'Controles de capital; canales informales' },
    { key: 'in', label: 'India', band: 'medio', fundamento: 'Efectivo intensivo; hawala' },
    { key: 'tr', label: 'Turquía', band: 'medio', fundamento: 'Salió de la gris GAFI (2024); corredores de efectivo' },
    { key: 'ae', label: 'Emiratos Árabes Unidos', band: 'medio', fundamento: 'Salió de la gris GAFI (2024); centro de secrecía y oro' },
    { key: 'ky', label: 'Islas Caimán', band: 'medio', fundamento: 'Offshore; salió de la gris GAFI (2023)' },
    { key: 'bs', label: 'Bahamas', band: 'medio', fundamento: 'Centro offshore regional' },
    { key: 'bb', label: 'Barbados', band: 'medio', fundamento: 'Salió de la gris GAFI (2024)' },
    { key: 'bz', label: 'Belice', band: 'medio', fundamento: 'Offshore societario regional' },
    { key: 'cw', label: 'Curazao', band: 'medio', fundamento: 'Centro offshore regional; juego online' },
    { key: 'gy', label: 'Guyana', band: 'medio', fundamento: 'Salió de la gris GAFI (2023); boom petrolero' },
    { key: 'jm', label: 'Jamaica', band: 'medio', fundamento: 'Salió de la gris GAFI (2024)' },
    { key: 'ch', label: 'Suiza', band: 'medio', fundamento: 'Secrecía bancaria histórica; CPI alto' },
    { key: 'lu', label: 'Luxemburgo', band: 'medio', fundamento: 'Vehículos de tenencia; centro financiero' },
    { key: 'hk', label: 'Hong Kong', band: 'medio', fundamento: 'Centro financiero; exposición a controles de China' },
    { key: 'cy', label: 'Chipre', band: 'medio', fundamento: 'Centro societario UE; banca no residente' },
    { key: 'mt', label: 'Malta', band: 'medio', fundamento: 'Salió de la gris GAFI (2022); juego online' },
    { key: 'al', label: 'Albania', band: 'medio', fundamento: 'Salió de la gris GAFI (2023)' },
    { key: 'rs', label: 'Serbia', band: 'medio', fundamento: 'CPI bajo' },
    { key: 'ua', label: 'Ucrania', band: 'medio', fundamento: 'Conflicto activo; controles de guerra. OJO: las regiones de Crimea, Donetsk y Luhansk están bajo sanciones OFAC comprensivas (nivel jurisdicción prohibida) — el catálogo es ISO-3166 país y no distingue región; tratar manualmente como bloqueo si el cliente tiene nexo con esas regiones' },
    { key: 'ph', label: 'Filipinas', band: 'medio', fundamento: 'Salió de la gris GAFI (2025); juego online' },
    { key: 'th', label: 'Tailandia', band: 'medio', fundamento: 'Efectivo; casinos de frontera' },
    { key: 'id', label: 'Indonesia', band: 'medio', fundamento: 'CPI bajo-medio; efectivo' },
    { key: 'eg', label: 'Egipto', band: 'medio', fundamento: 'CPI bajo; controles cambiarios' },
    { key: 'ma', label: 'Marruecos', band: 'medio', fundamento: 'Salió de la gris GAFI (2023)' },
    { key: 'jo', label: 'Jordania', band: 'medio', fundamento: 'Salió de la gris GAFI (2023)' },
    { key: 'sa', label: 'Arabia Saudita', band: 'medio', fundamento: 'CPI medio; corredores de remesas' },
    { key: 'qa', label: 'Catar', band: 'medio', fundamento: 'Centro financiero del Golfo' },
    { key: 'pk', label: 'Pakistán', band: 'medio', fundamento: 'Salió de la gris GAFI (2022); hawala' },
    { key: 'ug', label: 'Uganda', band: 'medio', fundamento: 'Salió de la gris GAFI (2024)' },
    { key: 'gr', label: 'Grecia', band: 'medio', fundamento: 'CPI medio; efectivo' },
    // — bajo —
    { key: 'us', label: 'Estados Unidos', band: 'bajo', fundamento: 'Supervisión FinCEN/OFAC; Basel banda baja' },
    { key: 'ca', label: 'Canadá', band: 'bajo', fundamento: 'Basel banda baja; CPI alto' },
    { key: 'gb', label: 'Reino Unido', band: 'bajo', fundamento: 'Supervisión FCA; CPI alto' },
    { key: 'de', label: 'Alemania', band: 'bajo', fundamento: 'UE núcleo; CPI alto' },
    { key: 'fr', label: 'Francia', band: 'bajo', fundamento: 'UE núcleo; CPI alto' },
    { key: 'es', label: 'España', band: 'bajo', fundamento: 'UE núcleo; SEPBLAC' },
    { key: 'it', label: 'Italia', band: 'bajo', fundamento: 'UE núcleo; CPI medio-alto' },
    { key: 'pt', label: 'Portugal', band: 'bajo', fundamento: 'UE núcleo' },
    { key: 'nl', label: 'Países Bajos', band: 'bajo', fundamento: 'UE núcleo; CPI alto' },
    { key: 'be', label: 'Bélgica', band: 'bajo', fundamento: 'UE núcleo' },
    { key: 'ie', label: 'Irlanda', band: 'bajo', fundamento: 'UE núcleo; CPI alto' },
    { key: 'at', label: 'Austria', band: 'bajo', fundamento: 'UE núcleo; CPI alto' },
    { key: 'se', label: 'Suecia', band: 'bajo', fundamento: 'CPI muy alto' },
    { key: 'no', label: 'Noruega', band: 'bajo', fundamento: 'CPI muy alto' },
    { key: 'dk', label: 'Dinamarca', band: 'bajo', fundamento: 'CPI muy alto' },
    { key: 'fi', label: 'Finlandia', band: 'bajo', fundamento: 'CPI muy alto' },
    { key: 'is', label: 'Islandia', band: 'bajo', fundamento: 'CPI muy alto' },
    { key: 'ee', label: 'Estonia', band: 'bajo', fundamento: 'CPI alto; supervisión UE' },
    { key: 'pl', label: 'Polonia', band: 'bajo', fundamento: 'UE; CPI medio-alto' },
    { key: 'cz', label: 'Chequia', band: 'bajo', fundamento: 'UE; CPI medio-alto' },
    { key: 'jp', label: 'Japón', band: 'bajo', fundamento: 'Basel banda baja; CPI alto' },
    { key: 'kr', label: 'Corea del Sur', band: 'bajo', fundamento: 'Basel banda baja' },
    { key: 'au', label: 'Australia', band: 'bajo', fundamento: 'AUSTRAC; CPI alto' },
    { key: 'nz', label: 'Nueva Zelanda', band: 'bajo', fundamento: 'CPI muy alto' },
    { key: 'sg', label: 'Singapur', band: 'bajo', fundamento: 'Supervisión MAS robusta; CPI muy alto' },
    { key: 'cl', label: 'Chile', band: 'bajo', fundamento: 'CPI alto regional' },
    { key: 'uy', label: 'Uruguay', band: 'bajo', fundamento: 'CPI alto regional' },
    { key: 'cr', label: 'Costa Rica', band: 'bajo', fundamento: 'CPI medio-alto regional' },
    { key: 'il', label: 'Israel', band: 'bajo', fundamento: 'Miembro GAFI; supervisión robusta' },
];
/** Nationality / country-of-incorporation → band (Acuerdo 1-2026 Art. 13.2 a/d). Searchable country list. */
export const RISK_NACIONALIDAD_CATALOG = RISK_COUNTRY_RISK_CATALOG;
/** Source/destination-of-funds → band (Acuerdo 1-2026 Art. 13.2 b/c): country corridor + the unverifiable case. */
export const RISK_ORIGEN_DESTINO_CATALOG = [
    RISK_COUNTRY_RISK_CATALOG[0],
    { key: 'no_verificable', label: 'Origen de los fondos no verificable', band: 'alto', fundamento: 'Ley 23: la fuente de fondos es parte de la DDC' },
    ...RISK_COUNTRY_RISK_CATALOG.slice(1),
];
/** Domicile / operating country → band (Acuerdo 1-2026 Art. 13.2 e): country + the no-physical-presence case. */
export const RISK_DOMICILIO_CATALOG = [
    RISK_COUNTRY_RISK_CATALOG[0],
    { key: 'sin_presencia', label: 'Sin presencia física verificable (domicilio virtual)', band: 'alto', fundamento: 'Señal FATF de sociedad pantalla (shell)' },
    ...RISK_COUNTRY_RISK_CATALOG.slice(1),
];
/** Product / service catalog → band for the single-variable producto factor (Acuerdo 1-2026 Art. 13.3). */
// El catálogo de producto se PODA por tipo de sujeto obligado (campo `modes`):
// cada tipo ofrece líneas de negocio distintas. Fundamento por entrada y por tipo:
// docs/indicadores-por-tipo.md (verificado contra Acuerdo SMV 6-2015, SSRP 7-2025,
// Ley 124/2020 Art. 40, Ley 23 Art. 22). Las entradas sin `modes` serían universales;
// aquí todas llevan `modes` porque el producto es el subfactor diferenciado por tipo.
export const RISK_PRODUCTO_CATALOG = [
    // — FINANCIERO (SBP/IPACOOP — Acuerdo 1-2026 Art. 13.3; Ley 23 Art. 22.1) —
    { key: 'cuenta_basica', label: 'Cuenta básica / ahorro de bajo saldo', band: 'bajo', modes: ['financiero'], fundamento: 'Baja exposición; tiering FATF de producto simple' },
    { key: 'cuenta_operativa', label: 'Cuenta corriente operativa (nómina, pagos)', band: 'bajo', modes: ['financiero'], fundamento: 'Flujos trazables congruentes con actividad' },
    { key: 'credito', label: 'Crédito / financiamiento con garantía', band: 'medio', modes: ['financiero'], fundamento: 'Riesgo moderado; el repago anticipado es señal UAF' },
    { key: 'inversion', label: 'Inversión / valores (banca)', band: 'medio', modes: ['financiero', 'valores'], fundamento: 'Riesgo moderado; sube con cuentas ómnibus o valores al portador' },
    { key: 'banca_privada', label: 'Banca privada / gestión patrimonial', band: 'alto', modes: ['financiero'], fundamento: 'Alta discrecionalidad y patrimonios complejos' },
    { key: 'corresponsalia', label: 'Corresponsalía bancaria', band: 'alto', modes: ['financiero'], fundamento: 'Riesgo indirecto del banco representado (FATF R.13)' },
    { key: 'comercio_exterior', label: 'Comercio exterior / cartas de crédito', band: 'alto', modes: ['financiero'], fundamento: 'TBML: sobre/sub-facturación (GAFI)' },
    { key: 'cripto', label: 'Criptoactivos / VASP', band: 'alto', modes: ['financiero'], fundamento: 'FATF R.15; sin régimen vigente en PA' },
    { key: 'remesas', label: 'Remesas / envío de dinero', band: 'alto', modes: ['financiero'], fundamento: 'Ley 23 Art. 22.1 (empresas de remesas); efectivo/transfronterizo (ENR)' },
    { key: 'cambio_divisas', label: 'Cambio de divisas', band: 'alto', modes: ['financiero'], fundamento: 'Ley 23 Art. 22.1 (casas de cambio); intensivo en efectivo' },
    { key: 'tarjetas_prepago', label: 'Tarjetas prepago / dinero electrónico', band: 'medio', modes: ['financiero'], fundamento: 'Ley 23 Art. 22.1 (emisores/procesadores); anonimato relativo del prepago' },
    { key: 'leasing_factoring', label: 'Arrendamiento financiero (leasing) / factoring', band: 'medio', modes: ['financiero'], fundamento: 'Ley 23 Art. 22.1 (leasing y factoring)' },
    { key: 'administracion_fideicomisos', label: 'Administración de fideicomisos (empresa fiduciaria)', band: 'alto', modes: ['financiero'], fundamento: 'Ley 23 Art. 22.1; Acuerdo 1-2026 (fiduciario); FATF R.25' },
    // — INTENSIVO EN EFECTIVO: financiero + inmobiliaria (cierre en efectivo) —
    { key: 'manejo_efectivo', label: 'Producto / operación intensiva en efectivo', band: 'alto', modes: ['financiero', 'inmobiliaria'], fundamento: 'El efectivo rompe la trazabilidad (Art. 28)' },
    // — CASAS DE VALORES (SMV — Acuerdo 6-2015) —
    { key: 'custodia_valores', label: 'Custodia de valores', band: 'medio', modes: ['valores'], fundamento: 'Acuerdo 6-2015 Art. 5 num.6 (riesgo de custodios/corresponsalía)' },
    { key: 'corresponsalia_valores', label: 'Corresponsalía de valores', band: 'alto', modes: ['valores'], fundamento: 'Acuerdo 6-2015 Art. 13 + Art. 16 num.4 (DD ampliada)' },
    { key: 'transferencias_internacionales', label: 'Transferencias internacionales de fondos/valores', band: 'alto', modes: ['valores'], fundamento: 'Acuerdo 6-2015 Art. 16 num.6 + Art. 10 num.2' },
    { key: 'efectivo_cuasi_efectivo', label: 'Recepción/manejo de efectivo y cuasi-efectivo', band: 'alto', modes: ['valores'], fundamento: 'Acuerdo 6-2015 Art. 16 num.5 + Art. 10 num.2' },
    { key: 'distribucion_cuotas_fondos', label: 'Distribución / suscripción de cuotas de fondos', band: 'medio', modes: ['valores'], fundamento: 'Acuerdo 6-2015 Art. 3 num.6-7 + Art. 10' },
    { key: 'asesoria_sin_custodia', label: 'Asesoría de inversión sin custodia', band: 'bajo', modes: ['valores'], fundamento: 'Acuerdo 6-2015 Art. 3 num.8 + Art. 5 num.6 a contrario (banda derivada)' },
    // — ASEGURADORAS (SSRP — Acuerdo 7-2025; Ley 12/2012 Art. 47) —
    { key: 'seguro_rescate', label: 'Vida individual con ahorro / valor de rescate / prima única', band: 'alto', modes: ['seguros'], fundamento: 'Ley 12/2012 Art. 47.1; Acuerdo 7-2025 Art. 15 a (excl. DDS) + Art. 17 c (EDD)' },
    { key: 'vida_devolucion_prima', label: 'Vida con devolución de prima pagada', band: 'alto', modes: ['seguros'], fundamento: 'Acuerdo 7-2025 Art. 15 c (excl. DDS por devolución de prima)' },
    { key: 'renta_vitalicia', label: 'Renta vitalicia / anualidad (prima única)', band: 'alto', modes: ['seguros'], fundamento: 'Ley 12/2012 Art. 47.1; Acuerdo 7-2025 Art. 15 a/c + Art. 17 c' },
    { key: 'fianzas', label: 'Fianzas (cumplimiento, pago, construcción)', band: 'medio', modes: ['seguros'], fundamento: 'Ley 12/2012 Art. 47.3; Acuerdo 7-2025 Art. 15 b (excl. DDS, sin EDD forzada)' },
    { key: 'ramos_generales', label: 'Ramos generales sin ahorro (incendio, auto, transporte, RC)', band: 'bajo', modes: ['seguros'], fundamento: 'Ley 12/2012 Art. 47.2; Acuerdo 7-2025 Art. 15 (DDS-elegible)' },
    { key: 'vida_colectiva', label: 'Vida colectiva / accidentes / salud (riesgo puro)', band: 'bajo', modes: ['seguros'], fundamento: 'Ley 12/2012 Art. 47.1; Acuerdo 7-2025 Art. 15 (DDS-elegible)' },
    { key: 'reaseguro', label: 'Reaseguro (cesión/aceptación entre regulados)', band: 'bajo', modes: ['seguros'], fundamento: 'Acuerdo 7-2025 Art. 3.5 (DD limitada por reliance regulatorio)' },
    // — INMOBILIARIA (SSNF — Ley 124/2020 Art. 40.3) —
    { key: 'corretaje_compraventa', label: 'Corretaje / intermediación en compraventa', band: 'medio', modes: ['inmobiliaria'], fundamento: 'Ley 124/2020 Art. 40.3; FATF R.22(b)' },
    { key: 'promocion_preventa', label: 'Promoción / desarrollo: preventa sobre planos, pagos escalonados', band: 'alto', modes: ['inmobiliaria'], fundamento: 'Ley 124/2020 Art. 40.3 (promotoras); preventa con anticipos y terceros pagadores (ENR)' },
    { key: 'compraventa_alto_valor', label: 'Compraventa de inmueble de alto valor / lujo', band: 'alto', modes: ['inmobiliaria'], fundamento: 'Ley 124/2020 Art. 40.3 + FATF ENR inmobiliario' },
    { key: 'financiamiento_promotor', label: 'Financiamiento directo del promotor (venta a plazos por el desarrollador)', band: 'medio', modes: ['inmobiliaria'], fundamento: 'Actividad conexa a la compraventa (Art. 40.3); no es crédito bancario SBP' },
    // — Tipos de inmueble (dueño, 2026-07-13): la línea de negocio por tipo de bien.
    //   La banda es del producto en sí; el salto a alto lo dan las señales del
    //   cliente/pago (tercero pagador, fuente no acreditable, efectivo en cierre).
    { key: 'residencia_unifamiliar', label: 'Residencia unifamiliar (vivienda propia)', band: 'bajo', modes: ['inmobiliaria'], fundamento: 'Uso propio típicamente hipotecado (el banco corre su propia DDC); con interés preferencial, precio topado e ingreso verificado' },
    { key: 'locales_comerciales', label: 'Locales comerciales', band: 'medio', modes: ['inmobiliaria'], fundamento: 'Comprador comercial/PJ con renta documentable; el riesgo lo dan las señales del cliente y del pago (calibración)' },
    { key: 'locales_industriales', label: 'Locales industriales / bodegas', band: 'medio', modes: ['inmobiliaria'], fundamento: 'Uso industrial verificable; comprador PJ — el riesgo escala por señales, no por el producto (calibración)' },
    { key: 'terrenos_inversion', label: 'Terrenos para inversión / futuro desarrollo', band: 'alto', modes: ['inmobiliaria'], fundamento: 'Valuación sin comparables → sub/sobrevaluación (tipología FATF ENR); land banking especulativo' },
    { key: 'edificios_alquiler', label: 'Edificios de alquiler (renta)', band: 'medio', modes: ['inmobiliaria'], fundamento: 'La integración vía flujo de alquileres es tipología ENR, pero con contratos e ingresos documentables; escalan las señales (efectivo, tercero pagador, fuente no acreditable)' },
    // — ABOGADOS Y GATEKEEPERS (SSNF — Ley 124/2020 Art. 40.11; FATF R.22) —
    { key: 'manejo_fondos_terceros', label: 'Manejo/administración de fondos de terceros (escrow/plica)', band: 'alto', modes: ['abogados'], fundamento: 'Ley 124/2020 Art. 40.11; FATF R.22(b)' },
    { key: 'constitucion_sociedades', label: 'Constitución y venta de sociedades / PJ', band: 'alto', modes: ['abogados'], fundamento: 'Ley 124/2020 Art. 40.11; FATF R.22(e)' },
    { key: 'agente_residente', label: 'Servicio de agente residente', band: 'alto', modes: ['abogados'], fundamento: 'Ley 124/2020 Art. 40.11; agente residente Ley 2/2011; FATF R.22(e)' },
    { key: 'fundaciones_fideicomisos', label: 'Constitución/administración de fundaciones y fideicomisos', band: 'alto', modes: ['abogados'], fundamento: 'Ley 124/2020 Art. 40.11; FATF R.22(e) + R.25' },
    { key: 'nominee', label: 'Provisión de nominee (director/dignatario/accionista nominal)', band: 'alto', modes: ['abogados'], fundamento: 'Ley 124/2020 Art. 40.11; FATF R.22 (opacidad del BF)' },
    { key: 'admin_cuentas_cliente', label: 'Administración de cuentas del cliente (firma autorizada)', band: 'alto', modes: ['abogados'], fundamento: 'Ley 124/2020 Art. 40.11; FATF R.22(c)' },
    { key: 'compraventa_inmueble_cliente', label: 'Compraventa de inmuebles por cuenta del cliente (cierre)', band: 'alto', modes: ['abogados'], fundamento: 'Ley 124/2020 Art. 40.11; FATF R.22(a)' },
    { key: 'escrituracion_notarial', label: 'Escrituración notarial (constitución, compraventa, poderes)', band: 'medio', modes: ['abogados'], fundamento: 'Ley 124/2020 Art. 40 (notarios); sube a alto si es constitución/transferencia de sociedades' },
    { key: 'auditoria_externa', label: 'Auditoría externa / estados financieros (contador)', band: 'medio', modes: ['abogados'], fundamento: 'Ley 124/2020 Art. 40.11 (contadores); sin manejo de fondos = moderado' },
    { key: 'servicios_tributarios', label: 'Servicios tributarios / planificación fiscal (contador)', band: 'medio', modes: ['abogados'], fundamento: 'Ley 124/2020 Art. 40.11; planificación agresiva = señal' },
    { key: 'asesoria_litigio_puro', label: 'Asesoría legal / litigio puro (sin actividad del Art. 40)', band: 'bajo', modes: ['abogados'], fundamento: 'Fuera del Art. 40.11; secreto profesional; KYC mínimo' },
];
/** Onboarding-channel catalog → band for canal.canal_via (Acuerdo 1-2026 Art. 13.4 d; Art. 14). */
export const RISK_CANAL_VIA_CATALOG = [
    { key: 'presencial', label: 'Presencial en oficina / sucursal', band: 'bajo', fundamento: 'Verificación cara a cara del documento y la persona' },
    { key: 'remoto_verificado', label: 'Remoto verificado (geolocalización inferencial + verificación de identidad)', band: 'medio', fundamento: 'Acuerdo 1-2026 Art. 14 (geolocalización inferencial obligatoria); Art. 20 (verificación reforzada de identidad)' },
    { key: 'remoto_simple', label: 'Remoto sin verificación reforzada', band: 'alto', fundamento: 'No presencial sin mitigantes del Art. 14' },
    { key: 'intermediario', label: 'Vía intermediario / presentador (tercero)', band: 'alto', fundamento: 'La DDC descansa en un tercero; responsabilidad no delegable' },
    // Corredor de bienes raíces (dueño, 2026-07-13): la intermediación ES la vía
    // estándar del sector y el corredor es él mismo sujeto obligado con DD propia —
    // no equivale al "intermediario" genérico (alto). La DDC sigue siendo no
    // delegable, por eso medio y no bajo.
    { key: 'corredor_bienes_raices', label: 'Vía corredor de bienes raíces (intermediación estándar del sector)', band: 'medio', modes: ['inmobiliaria'], fundamento: 'Ley 124/2020 Art. 40.3: el corredor es sujeto obligado propio con DD paralela; la responsabilidad de DDC del vendedor/promotor no se delega' },
];
/** Transaction-pattern catalog → band for canal.frecuencia (Acuerdo 1-2026 Art. 13.4 c; UAF Señales). */
export const RISK_FRECUENCIA_CATALOG = [
    { key: 'estable', label: 'Estable y congruente con el perfil', band: 'bajo', fundamento: 'Patrón esperado del perfil declarado' },
    { key: 'estacional', label: 'Variable pero explicada (estacionalidad del negocio)', band: 'medio', fundamento: 'Variabilidad con explicación documentada' },
    { key: 'reactivada', label: 'Cuenta dormida reactivada súbitamente', band: 'alto', fundamento: 'Señal expresa del catálogo UAF' },
    { key: 'rafagas', label: 'Ráfagas de operaciones en corto tiempo', band: 'alto', fundamento: 'Velocidad incongruente (catálogo UAF)' },
    { key: 'estructuracion', label: 'Estructuración / fraccionamiento bajo el umbral', band: 'alto', fundamento: 'Smurfing: señal expresa UAF' },
];
/** Amount-vs-profile catalog → band for canal.monto (Acuerdo 1-2026 Art. 13.4 b). */
export const RISK_MONTO_CATALOG = [
    { key: 'congruente', label: 'Congruente con el perfil declarado', band: 'bajo', fundamento: 'Sin brecha entre montos y perfil' },
    { key: 'rte_ocasional', label: 'Sobre umbral RTE (B/.10,000) ocasional', band: 'medio', fundamento: 'El umbral RTE obliga a reportar, no a ponderar' },
    { key: 'rte_recurrente', label: 'Sobre umbral RTE recurrente', band: 'medio', fundamento: 'Recurrencia esperada solo si el perfil la explica' },
    { key: 'incongruente', label: 'Múltiplos del umbral / incongruente con el perfil', band: 'alto', fundamento: 'La incongruencia (no el monto absoluto) es lo que pesa' },
];
/** Payment-instrument catalog → band for canal.instrumento (Acuerdo 1-2026 Art. 13.4 a; Art. 28). */
export const RISK_INSTRUMENTO_CATALOG = [
    { key: 'transferencia', label: 'Transferencia bancaria trazable (ACH / SWIFT)', band: 'bajo', fundamento: 'Trazabilidad completa origen-destino' },
    { key: 'tarjeta', label: 'Tarjeta / pago electrónico trazable', band: 'bajo', fundamento: 'Rastro electrónico verificable' },
    { key: 'cheque', label: 'Cheque', band: 'medio', fundamento: 'Semi-trazable; endosos debilitan el rastro' },
    { key: 'cheque_gerencia', label: 'Cheque de gerencia / giros bancarios', band: 'medio', fundamento: 'Semi-trazable; uso en cierres de alto valor' },
    { key: 'efectivo', label: 'Efectivo', band: 'alto', fundamento: 'Rompe la trazabilidad (Art. 28)' },
    { key: 'cuasi_efectivo', label: 'Cuasi-efectivo (money orders, cheques de viajero)', band: 'alto', fundamento: 'Equivalente funcional del efectivo' },
    { key: 'cripto', label: 'Criptoactivos', band: 'alto', fundamento: 'FATF R.15; pseudonimato del instrumento' },
];
/**
 * Catalog per simulator input — keyed `family.subfactor` (or `producto` for the
 * single-variable factor). Every leaf the officer classifies has a concrete,
 * plain-language catalog: pick the situation, the band derives from the
 * documented mapping — nobody has to guess what bajo/medio/alto means.
 */
export const RISK_SUBFACTOR_CATALOGS = {
    'cliente.profesion': RISK_PROFESSION_CATALOG,
    'cliente.estructura': RISK_ESTRUCTURA_CATALOG,
    'cliente.antecedentes': RISK_ANTECEDENTES_CATALOG,
    'jurisdiccion.nacionalidad': RISK_NACIONALIDAD_CATALOG,
    'jurisdiccion.origen_destino': RISK_ORIGEN_DESTINO_CATALOG,
    'jurisdiccion.domicilio': RISK_DOMICILIO_CATALOG,
    'jurisdiccion.listas': RISK_COUNTRY_LIST_CATALOG,
    producto: RISK_PRODUCTO_CATALOG,
    'canal.canal_via': RISK_CANAL_VIA_CATALOG,
    'canal.frecuencia': RISK_FRECUENCIA_CATALOG,
    'canal.monto': RISK_MONTO_CATALOG,
    'canal.instrumento': RISK_INSTRUMENTO_CATALOG,
};
/**
 * Which override an `override: true` catalog entry fires, per catalog. Only the
 * catalogs whose alto extreme maps to a documented OVERRIDE (raise-the-floor
 * indicator, outside the weighted arithmetic) appear here.
 */
export const CATALOG_OVERRIDE_KEY = {
    'jurisdiccion.listas': 'fatfNegra',
    // País GAFI-negra / sanciones integrales dispara el mismo override venga de
    // nacionalidad, corredor de fondos o domicilio (FATF R.19).
    'jurisdiccion.nacionalidad': 'fatfNegra',
    'jurisdiccion.origen_destino': 'fatfNegra',
    'jurisdiccion.domicilio': 'fatfNegra',
    'cliente.antecedentes': 'antecedente',
};
export const RISK_INDICATOR_REFERENCE = [
    {
        key: 'sanciones',
        label: 'Sanciones (OFAC / UE / ONU / UK)',
        efecto: 'CRÍTICO — eleva a alto + bloquea alta automática (congelamiento vía listas ONU); OFAC/UE/UK: política de riesgo, no vinculantes directamente en Panamá',
        fundamento: 'Ley 23 Arts. 49-52 + DE 587/2015 (listas CSNU); FATF R.6/R.7',
    },
    {
        key: 'pep',
        label: 'PEP (nacional / extranjero + allegados)',
        efecto: 'Piso "alto" + EDD (no bloquea)',
        fundamento: 'Acuerdo 5-2015 Art. 9 (otros sujetos SBP) / Acuerdo 1-2026 Arts. 20 in fine y 27 (bancos y fiduciarias); Ley 23 Art. 4.18; FATF R.12',
    },
    {
        key: 'fatfNegra',
        label: 'FATF lista negra (país)',
        efecto: 'Eleva a alto + EDD; bloqueo como contramedida/política interna (Irán/RPDC); congelamiento de designados solo vía Ley 23 Art. 49 (Res. ONU 1737/1718)',
        fundamento: 'FATF R.19 + INR.19 §2(e); Acuerdo 1-2026 Art. 20 in fine num. 3 y Art. 21',
    },
    {
        key: 'antecedente',
        label: 'Antecedentes penales (BAFA)',
        efecto: 'Piso "alto" + EDD (revisión humana, nunca adverso automático)',
        fundamento: 'CP Art. 254 (delito precedente del blanqueo); política interna con garantía de revisión humana (debido proceso)',
    },
];
//# sourceMappingURL=risk-score.js.map